import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", state: "", license_number: "", agd_number: "", password: "" };

export function StudentFormDialog({ open, onClose, student, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("session_token");
  const isEdit = Boolean(student);

  useEffect(() => {
    if (open) setForm(student ? { ...EMPTY, ...student, password: "" } : EMPTY);
  }, [open, student]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      const body = new FormData();
      ["name", "email", "state", "license_number", "agd_number"].forEach((k) => body.append(k, form[k] || ""));
      if (!isEdit && form.password) body.append("password", form.password);
      const headers = { Authorization: `Bearer ${token}` };
      if (isEdit) await axios.put(`${API}/admin/students/${student.id}`, body, { headers });
      else await axios.post(`${API}/admin/students`, body, { headers });
      toast.success(isEdit ? "Student updated" : "Student added");
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save student");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="student-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Student" : "Add Student"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Full name *</Label>
            <Input value={form.name} onChange={set("name")} placeholder="Dr. Jane Smith" data-testid="student-form-name" />
          </div>
          <div>
            <Label className="text-xs">Email *</Label>
            <Input type="email" value={form.email} onChange={set("email")} placeholder="jane@example.com" data-testid="student-form-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">State</Label>
              <Input value={form.state} onChange={set("state")} placeholder="TN" maxLength={2} data-testid="student-form-state" />
            </div>
            <div>
              <Label className="text-xs">License #</Label>
              <Input value={form.license_number} onChange={set("license_number")} data-testid="student-form-license" />
            </div>
          </div>
          <div>
            <Label className="text-xs">AGD #</Label>
            <Input value={form.agd_number} onChange={set("agd_number")} data-testid="student-form-agd" />
          </div>
          {!isEdit && (
            <div>
              <Label className="text-xs">Password (optional)</Label>
              <Input type="text" value={form.password} onChange={set("password")} placeholder="Leave blank — student can use Forgot Password" data-testid="student-form-password" />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="student-form-cancel">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#102862] hover:bg-[#1a3a7a] text-white" data-testid="student-form-save">
              {saving ? "Saving..." : isEdit ? "Save changes" : "Add student"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
